<template>
  <component
    :is="'to' in link ? RouterLink : isExternal ? 'a' : 'button'"
    v-if="('href' in link && link.href != null) || 'to' in link"
    :to="'to' in link ? link.to : undefined"
    class="ui-help-link inline-flex items-center"
    v-bind="{ ...linkProps, ...$attrs }"
    @click="handleClick"
  >
    <v-icon class="text-lg" color="primary"
      >mdi-book-open-variant-outline</v-icon
    >&nbsp;
    <span class="inline-flex items-baseline gap-1 text-xs font-medium">
      <span class="ui-help-link-content text-left">
        <slot>
          {{ link.title }}
        </slot>
      </span>
      <v-icon v-if="isExternal" size="xs">mdi-open-in-new</v-icon>
    </span>
  </component>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { type RouteLocationRaw, RouterLink } from 'vue-router';

export type IHelpLink =
  | { title: string; href: string | null }
  | { title: string; to: RouteLocationRaw };

interface Props {
  link: IHelpLink;
}

const props = defineProps<Props>();
const emit = defineEmits<{ click: [Event] }>();

const isExternal = computed(
  () => 'href' in props.link && props.link.href?.startsWith('http')
);

const linkProps = computed(() =>
  isExternal.value
    ? {
        target: '_blank',
        rel: 'noopener noreferrer',
        href: 'href' in props.link ? props.link.href : undefined,
      }
    : { type: 'button' }
);

function handleClick(event: Event) {
  if (isExternal.value || 'to' in props.link) return;
  emit('click', event);
}
</script>

<style scoped lang="scss">
.ui-help-link {
  color: rgb(var(--v-theme-primary, 25, 118, 210));
  text-decoration: none;

  .ui-help-link-content {
    text-decoration: none;
    cursor: pointer;
    text-align: center;
    padding-left: 4px;
  }

  &:hover .ui-help-link-content {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
  }

  :deep(.v-icon) {
    margin-top: -3px;
  }
}
</style>
